'use client';

import { useState } from 'react';
import {
  X,
  Plus,
  Trash2,
  Users,
  ArrowRight,
  Shuffle,
  Clock,
  Play,
  Square,
  ChevronDown,
  ChevronUp,
  AlertCircle
} from 'lucide-react';
import { notify } from '@/lib/notifications';

interface Participant {
  id: number;
  name: string;
  avatar?: string;
}

interface BreakoutRoom {
  id: string;
  name: string;
  participants: Participant[];
}

interface BreakoutRoomsModalProps {
  isOpen: boolean;
  onClose: () => void;
  participants: Participant[];
  onStartBreakout: (rooms: BreakoutRoom[], duration?: number) => void;
  onEndBreakout: () => void;
  isBreakoutActive: boolean;
  activeRooms?: BreakoutRoom[];
  currentUserId: number;
  isHost: boolean;
}

export default function BreakoutRoomsModal({
  isOpen,
  onClose,
  participants,
  onStartBreakout,
  onEndBreakout,
  isBreakoutActive,
  activeRooms = [],
  currentUserId,
  isHost
}: BreakoutRoomsModalProps) {
  const [rooms, setRooms] = useState<BreakoutRoom[]>([]);
  const [duration, setDuration] = useState<number>(15); // minutes
  const [showTimer, setShowTimer] = useState(false);
  const [unassigned, setUnassigned] = useState<Participant[]>([]);
  const [draggedParticipant, setDraggedParticipant] = useState<Participant | null>(null);
  const [expandedRooms, setExpandedRooms] = useState<Set<string>>(new Set());

  // Initialize with participants — render-phase state adjustment keyed on the
  // inputs that previously drove the effect (open/participants/active).
  const [initKey, setInitKey] = useState<Participant[] | null>(null);
  const openInitKey = isOpen && !isBreakoutActive ? participants : null;
  if (openInitKey !== initKey) {
    setInitKey(openInitKey);
    if (openInitKey) {
      setUnassigned([...participants]);
      setRooms([
        { id: 'room-1', name: 'Room 1', participants: [] },
        { id: 'room-2', name: 'Room 2', participants: [] }
      ]);
    }
  }

  // Update rooms if breakout is active — same pattern, keyed on activeRooms identity
  const [syncedActiveRooms, setSyncedActiveRooms] = useState<BreakoutRoom[] | null>(null);
  if (isBreakoutActive && activeRooms.length > 0 && syncedActiveRooms !== activeRooms) {
    setSyncedActiveRooms(activeRooms);
    setRooms(activeRooms);
  }

  // Add a new room
  const addRoom = () => {
    const newId = `room-${rooms.length + 1}`;
    setRooms([...rooms, { id: newId, name: `Room ${rooms.length + 1}`, participants: [] }]);
    setExpandedRooms(prev => new Set([...prev, newId]));
  };

  // Remove a room
  const removeRoom = (roomId: string) => {
    const room = rooms.find(r => r.id === roomId);
    if (room) {
      // Return participants to unassigned
      setUnassigned([...unassigned, ...room.participants]);
      setRooms(rooms.filter(r => r.id !== roomId));
    }
  };

  // Rename a room
  const renameRoom = (roomId: string, newName: string) => {
    setRooms(rooms.map(r => r.id === roomId ? { ...r, name: newName } : r));
  };

  // Move participant to a room
  const moveParticipant = (participant: Participant, toRoomId: string | null) => {
    // Remove from current location
    setUnassigned(unassigned.filter(p => p.id !== participant.id));
    setRooms(rooms.map(r => ({
      ...r,
      participants: r.participants.filter(p => p.id !== participant.id)
    })));

    // Add to new location
    if (toRoomId === null) {
      setUnassigned(prev => [...prev, participant]);
    } else {
      setRooms(rooms.map(r => 
        r.id === toRoomId 
          ? { ...r, participants: [...r.participants, participant] }
          : r
      ));
    }
  };

  // Auto-assign participants randomly
  const shuffleParticipants = () => {
    const allParticipants = [
      ...unassigned,
      ...rooms.flatMap(r => r.participants)
    ];
    
    // Shuffle
    const shuffled = [...allParticipants].sort(() => Math.random() - 0.5);
    
    // Distribute evenly
    const perRoom = Math.ceil(shuffled.length / rooms.length);
    const newRooms = rooms.map((room, index) => ({
      ...room,
      participants: shuffled.slice(index * perRoom, (index + 1) * perRoom)
    }));
    
    setRooms(newRooms);
    setUnassigned([]);
  };

  // Handle drag start
  const handleDragStart = (participant: Participant) => {
    setDraggedParticipant(participant);
  };

  // Handle drop
  const handleDrop = (roomId: string | null) => {
    if (draggedParticipant) {
      moveParticipant(draggedParticipant, roomId);
      setDraggedParticipant(null);
    }
  };

  // Toggle room expansion
  const toggleRoomExpansion = (roomId: string) => {
    setExpandedRooms(prev => {
      const next = new Set(prev);
      if (next.has(roomId)) {
        next.delete(roomId);
      } else {
        next.add(roomId);
      }
      return next;
    });
  };

  // Start breakout session
  const handleStart = () => {
    const filledRooms = rooms.filter(r => r.participants.length > 0);
    if (filledRooms.length < 2) {
      notify.warning('Please assign participants to at least 2 rooms');
      return;
    }
    onStartBreakout(filledRooms, showTimer ? duration : undefined);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-blue-400" />
            <h2 className="text-lg font-semibold text-white">Breakout Rooms</h2>
            {isBreakoutActive && (
              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                Active
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isBreakoutActive ? (
            // Active breakout view
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-amber-400 bg-amber-500/10 rounded-lg p-3">
                <AlertCircle className="w-5 h-5" />
                <span className="text-sm">Breakout session is in progress</span>
              </div>
              
              {rooms.map(room => (
                <div key={room.id} className="bg-gray-700/50 rounded-lg overflow-hidden">
                  <div
                    className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-700/70"
                    onClick={() => toggleRoomExpansion(room.id)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-white">{room.name}</span>
                      <span className="text-xs text-gray-400">
                        {room.participants.length} participant{room.participants.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {expandedRooms.has(room.id) ? (
                      <ChevronUp className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                  
                  {expandedRooms.has(room.id) && (
                    <div className="px-4 pb-3 space-y-2">
                      {room.participants.map(p => (
                        <div key={p.id} className="flex items-center gap-2 text-sm text-gray-300">
                          <div className="w-6 h-6 rounded-full bg-gray-600 flex items-center justify-center text-xs">
                            {p.name.charAt(0).toUpperCase()}
                          </div>
                          {p.name}
                          {p.id === currentUserId && (
                            <span className="text-xs text-blue-400">(You)</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            // Setup view
            <div className="space-y-6">
              {/* Unassigned participants */}
              <div>
                <h3 className="text-sm font-medium text-gray-400 mb-2">
                  Unassigned ({unassigned.length})
                </h3>
                <div
                  className="min-h-[60px] p-3 rounded-lg border-2 border-dashed border-gray-600 flex flex-wrap gap-2"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(null)}
                >
                  {unassigned.map(p => (
                    <div
                      key={p.id}
                      draggable
                      onDragStart={() => handleDragStart(p)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 rounded-full text-sm text-white cursor-move hover:bg-gray-600 transition-colors"
                    >
                      <div className="w-5 h-5 rounded-full bg-gray-500 flex items-center justify-center text-xs">
                        {p.name.charAt(0).toUpperCase()}
                      </div>
                      {p.name}
                    </div>
                  ))}
                  {unassigned.length === 0 && (
                    <span className="text-gray-500 text-sm">Drop participants here</span>
                  )}
                </div>
              </div>

              {/* Rooms */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-400">Rooms ({rooms.length})</h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={shuffleParticipants}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <Shuffle className="w-4 h-4" />
                      Shuffle
                    </button>
                    <button
                      onClick={addRoom}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Room
                    </button>
                  </div>
                </div>

                {rooms.map((room, index) => (
                  <div
                    key={room.id}
                    className="bg-gray-700/50 rounded-lg overflow-hidden"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDrop(room.id)}
                  >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-600">
                      <input
                        type="text"
                        value={room.name}
                        onChange={(e) => renameRoom(room.id, e.target.value)}
                        className="bg-transparent text-white font-medium focus:outline-none focus:ring-1 focus:ring-blue-500 rounded px-1"
                      />
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">
                          {room.participants.length} participant{room.participants.length !== 1 ? 's' : ''}
                        </span>
                        {rooms.length > 2 && (
                          <button
                            onClick={() => removeRoom(room.id)}
                            className="p-1 text-gray-400 hover:text-red-400 rounded transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="p-3 min-h-[50px] flex flex-wrap gap-2">
                      {room.participants.map(p => (
                        <div
                          key={p.id}
                          draggable
                          onDragStart={() => handleDragStart(p)}
                          className="flex items-center gap-2 px-3 py-1.5 bg-gray-600 rounded-full text-sm text-white cursor-move hover:bg-gray-500 transition-colors"
                        >
                          <div className="w-5 h-5 rounded-full bg-gray-400 flex items-center justify-center text-xs">
                            {p.name.charAt(0).toUpperCase()}
                          </div>
                          {p.name}
                        </div>
                      ))}
                      {room.participants.length === 0 && (
                        <span className="text-gray-500 text-sm">Drop participants here</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Timer option */}
              <div className="flex items-center gap-4 p-4 bg-gray-700/30 rounded-lg">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showTimer}
                    onChange={(e) => setShowTimer(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-500 text-blue-500 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-300">Set timer</span>
                </label>
                {showTimer && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <input
                      type="number"
                      value={duration}
                      onChange={(e) => setDuration(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      min="1"
                      max="120"
                    />
                    <span className="text-sm text-gray-400">minutes</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          {isHost && (
            isBreakoutActive ? (
              <button
                onClick={onEndBreakout}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Square className="w-4 h-4" />
                End Breakout
              </button>
            ) : (
              <button
                onClick={handleStart}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Play className="w-4 h-4" />
                Start Breakout
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
